import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Tabs,
  Tab,
  Paper,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  Snackbar,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import api from "../services/api";
import ApprovalChainRulesManager from "../components/admin/ApprovalChainRulesManager";
import GradeManagement from "../components/admin/GradeManagement";
import DesignationManagement from "../components/admin/DesignationManagement";
import EmploymentHistoryManagement from "../components/admin/EmploymentHistoryManagement";
import LeaveTypeManagement from "../components/hr/LeaveTypeManagement";
import AuditLogManagement from "../components/admin/AuditLogManagement";
import DatabaseBackupManagement from "../components/admin/DatabaseBackupManagement";
import ManualLeaveEntryManagement from "../components/admin/ManualLeaveEntryManagement";
import HolidayManagement from "../components/admin/HolidayManagement";
import RoleNotificationManagement from "../components/admin/RoleNotificationManagement";

interface AttachmentRule {
  minDays: number;
  maxDays: number | string;
  requirementType: string;
  description: string;
}

interface ApprovalStep {
  level: number;
  role: string;
  action_type?: string;
}

interface ApprovalRule {
  min_days: number;
  max_days: number | null;
  approval_chain: ApprovalStep[];
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminPanel: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [departments, setDepartments] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [employees, setEmployees] = useState<any[]>([]);
  const [leaveTypes, setLeaveTypes] = useState<any[]>([]);
  const [entitlements, setEntitlements] = useState<any[]>([]);
  const [designations, setDesignations] = useState<any[]>([]);
  const [vacationPeriods, setVacationPeriods] = useState<any[]>([]);
  const [attachmentRequirements, setAttachmentRequirements] = useState<any[]>(
    [],
  );
  const [grades, setGrades] = useState<any[]>([]);
  const [approvalChains, setApprovalChains] = useState<any[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog states
  const [openUnitDialog, setOpenUnitDialog] = useState(false);
  const [openCategoryDialog, setOpenCategoryDialog] = useState(false);
  const [openEmployeeDialog, setOpenEmployeeDialog] = useState(false);
  const [openBulkUploadDialog, setOpenBulkUploadDialog] = useState(false);
  const [openLeaveBalanceDialog, setOpenLeaveBalanceDialog] = useState(false);
  const [openEntitlementDialog, setOpenEntitlementDialog] = useState(false);
  const [openDesignationDialog, setOpenDesignationDialog] = useState(false);
  const [openVacationDialog, setOpenVacationDialog] = useState(false);
  const [openAttachmentDialog, setOpenAttachmentDialog] = useState(false);
  const [editingItem, setEditingItem] = useState<any>(null);

  // Form states
  const [departmentForm, setDepartmentForm] = useState({
    name: "",
    code: "",
    description: "",
    headEmployeeId: "",
    parentDepartmentId: "",
  });
  const [employeeForm, setEmployeeForm] = useState({
    name: "",
    email: "",
    contact_number: "",
    nic_number: "",
    employee_code: "",
    department_id: "",
    category_id: "",
    role: "staff",
    designation: "",
    designation_id: "",
    grade_id: "",
    join_date: "",
  });
  const [categoryForm, setCategoryForm] = useState({
    name: "",
    description: "",
  });
  const [entitlementForm, setEntitlementForm] = useState({
    categoryId: "",
    leaveTypeId: "",
    maxDaysPerYear: 0,
    carryForwardDays: 0,
    maxAccumulation: 0,
    requires_substitute_acceptance: 1,
    attachment_rules: [] as AttachmentRule[],
    approval_rules: [] as ApprovalRule[],
  });
  const [designationForm, setDesignationForm] = useState({
    name: "",
    code: "",
    description: "",
  });
  const [vacationForm, setVacationForm] = useState({
    name: "",
    departmentId: "",
    categoryId: "",
    leaveTypeId: "",
    startDate: "",
    endDate: "",
    description: "",
  });
  const [attachmentForm, setAttachmentForm] = useState({
    leaveTypeId: "",
    minDays: 0,
    maxDays: "",
    requirementType: "not_required",
    description: "",
  });
  const [leaveBalanceForm, setLeaveBalanceForm] = useState({
    employeeId: "",
    leaveTypeId: "",
    totalEntitlement: 0,
    usedDays: 0,
    year: new Date().getFullYear(),
  });

  // Bulk upload states
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadResults, setUploadResults] = useState<any>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [
        departmentsRes,
        categoriesRes,
        employeesRes,
        leaveTypesRes,
        entitlementsRes,
        designationsRes,
        vacationPeriodsRes,
        attachmentReqRes,
        leaveBalancesRes,
        gradesRes,
      ] = await Promise.all([
        api.get("/admin/departments").catch(() => ({ data: [] })),
        api.get("/admin/categories").catch(() => ({ data: [] })),
        api.get("/admin/employees").catch(() => ({ data: [] })),
        api.get("/admin/leave-types").catch(() => ({ data: [] })),
        api.get("/admin/leave-entitlements").catch(() => ({ data: [] })),
        api.get("/admin/designations").catch(() => ({ data: [] })),
        api.get("/admin/vacation-periods").catch(() => ({ data: [] })),
        api.get("/admin/attachment-requirements").catch(() => ({ data: [] })),
        api.get("/admin/leave-balances").catch(() => ({ data: [] })),
        api.get("/grades").catch(() => ({ data: [] })),
      ]);
      setDepartments(departmentsRes.data || []);
      setCategories(categoriesRes.data || []);
      setEmployees(employeesRes.data || []);
      setLeaveTypes(leaveTypesRes.data || []);
      setEntitlements(entitlementsRes.data || []);
      setDesignations(designationsRes.data || []);
      setVacationPeriods(vacationPeriodsRes.data || []);
      setAttachmentRequirements(attachmentReqRes.data || []);
      setLeaveBalances(leaveBalancesRes.data || []);
      setGrades(gradesRes.data || []);
    } catch (err) {
      console.error("Failed to load data", err);
      setError("Failed to load data");
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Organization Unit handlers
  const handleAddDepartment = async () => {
    try {
      await api.post("/admin/departments", departmentForm);
      setSuccess("Department added successfully");
      setOpenUnitDialog(false);
      setDepartmentForm({
        name: "",
        code: "",
        description: "",
        headEmployeeId: "",
        parentDepartmentId: "",
      });
      loadData();
    } catch (err) {
      setError("Failed to add department");
    }
  };

  const handleEditDepartment = async (id: number) => {
    try {
      await api.put(`/admin/departments/${id}`, departmentForm);
      setSuccess("Department updated successfully");
      setOpenUnitDialog(false);
      setDepartmentForm({
        name: "",
        code: "",
        description: "",
        headEmployeeId: "",
        parentDepartmentId: "",
      });
      setEditingItem(null);
      loadData();
    } catch (err) {
      setError("Failed to update department");
    }
  };

  const handleCloseUnitDialog = () => {
    setOpenUnitDialog(false);
    setDepartmentForm({
      name: "",
      code: "",
      description: "",
      headEmployeeId: "",
      parentDepartmentId: "",
    });
    setEditingItem(null);
  };

  const handleDeleteDepartment = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this department?")) {
      try {
        await api.delete(`/admin/departments/${id}`);
        setSuccess("Department deleted successfully");
        loadData();
      } catch (err) {
        setError("Failed to delete department");
      }
    }
  };

  // Employee handlers
  const handleAddEmployee = async () => {
    try {
      // Adapt form shape to backend expected fields
      await api.post("/admin/employees", {
        name: employeeForm.name,
        email: employeeForm.email,
        contactNumber: employeeForm.contact_number,
        nicNumber: employeeForm.nic_number,
        employeeCode: employeeForm.employee_code,
        departmentId: employeeForm.department_id,
        categoryId: employeeForm.category_id,
        designation: employeeForm.designation, // Legacy fallback
        designationId: employeeForm.designation_id || null,
        gradeId: employeeForm.grade_id || null,
        role: employeeForm.role,
        joinDate: employeeForm.join_date || null,
      });
      setSuccess("Employee added successfully");
      setOpenEmployeeDialog(false);
      setEmployeeForm({
        name: "",
        email: "",
        contact_number: "",
        nic_number: "",
        employee_code: "",
        department_id: "",
        category_id: "",
        role: "staff",
        designation: "",
        designation_id: "",
        grade_id: "",
        join_date: "",
      });
      loadData();
    } catch (err) {
      setError("Failed to add employee");
    }
  };

  const handleEditEmployee = async (id: number) => {
    try {
      await api.put(`/admin/employees/${id}`, {
        name: employeeForm.name,
        email: employeeForm.email,
        contactNumber: employeeForm.contact_number,
        nicNumber: employeeForm.nic_number,
        employeeCode: employeeForm.employee_code,
        departmentId: employeeForm.department_id,
        categoryId: employeeForm.category_id,
        designation: employeeForm.designation, // Legacy fallback
        designationId: employeeForm.designation_id || null,
        gradeId: employeeForm.grade_id || null,
        role: employeeForm.role,
        joinDate: employeeForm.join_date || null,
      });
      setSuccess("Employee updated successfully");
      setOpenEmployeeDialog(false);
      setEmployeeForm({
        name: "",
        email: "",
        contact_number: "",
        nic_number: "",
        employee_code: "",
        department_id: "",
        category_id: "",
        role: "staff",
        designation: "",
        designation_id: "",
        grade_id: "",
        join_date: "",
      });
      loadData();
    } catch (err) {
      setError("Failed to update employee");
    }
  };

  const handleDeleteEmployee = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this employee?")) {
      try {
        await api.delete(`/admin/employees/${id}`);
        setSuccess("Employee deleted successfully");
        loadData();
      } catch (err) {
        setError("Failed to delete employee");
      }
    }
  };

  // Bulk upload handlers
  const handleDownloadTemplate = async () => {
    try {
      const response = await api.get("/admin/employees/bulk/template", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `staff_upload_template_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess("Template downloaded successfully");
    } catch (err) {
      setError("Failed to download template");
    }
  };

  const handleDownloadAllEmployees = async () => {
    try {
      const response = await api.get("/admin/employees/export/all", {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `all_employees_${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      setSuccess("Employee data downloaded successfully");
    } catch (err) {
      setError("Failed to download employee data");
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith(".csv") && !file.name.endsWith(".txt")) {
        setError("Please upload a CSV file");
        return;
      }
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError("File size must be less than 5MB");
        return;
      }
      setUploadFile(file);
      setUploadResults(null);
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) {
      setError("Please select a file to upload");
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append("file", uploadFile);

    try {
      const response = await api.post(
        "/admin/employees/bulk/upload",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );

      setUploadResults(response.data);
      setSuccess(
        `Upload completed: ${response.data.summary.success} successful, ${response.data.summary.failed} failed`,
      );
      loadData(); // Refresh employee list
    } catch (err: any) {
      setError(err.response?.data?.error || "Failed to upload file");
    } finally {
      setUploading(false);
    }
  };

  const handleCloseBulkUpload = () => {
    setOpenBulkUploadDialog(false);
    setUploadFile(null);
    setUploadResults(null);
  };

  // Category handlers
  const handleAddCategory = async () => {
    try {
      await api.post("/admin/categories", categoryForm);
      setSuccess("Category added successfully");
      setOpenCategoryDialog(false);
      setCategoryForm({ name: "", description: "" });
      loadData();
    } catch (err) {
      setError("Failed to add category");
    }
  };

  const handleEditCategory = async (id: number) => {
    try {
      await api.put(`/admin/categories/${id}`, categoryForm);
      setSuccess("Category updated successfully");
      setOpenCategoryDialog(false);
      setCategoryForm({ name: "", description: "" });
      setEditingItem(null);
      loadData();
    } catch (err) {
      setError("Failed to update category");
    }
  };

  const handleDeleteCategory = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this category?")) {
      try {
        await api.delete(`/admin/categories/${id}`);
        setSuccess("Category deleted successfully");
        loadData();
      } catch (err) {
        setError("Failed to delete category");
      }
    }
  };

  // Entitlement handlers
  const handleAddEntitlement = async () => {
    try {
      await api.post("/admin/leave-entitlements", entitlementForm);
      setSuccess("Leave entitlement added successfully");
      setOpenEntitlementDialog(false);
      setEntitlementForm({
        categoryId: "",
        leaveTypeId: "",
        maxDaysPerYear: 0,
        carryForwardDays: 0,
        maxAccumulation: 0,
        requires_substitute_acceptance: 1,
        attachment_rules: [],
        approval_rules: [],
      });
      loadData();
    } catch (err) {
      setError("Failed to add leave entitlement");
    }
  };

  const handleEditEntitlement = async (id: number) => {
    try {
      await api.put(`/admin/leave-entitlements/${id}`, entitlementForm);
      setSuccess("Leave entitlement updated successfully");
      setOpenEntitlementDialog(false);
      setEntitlementForm({
        categoryId: "",
        leaveTypeId: "",
        maxDaysPerYear: 0,
        carryForwardDays: 0,
        maxAccumulation: 0,
        requires_substitute_acceptance: 1,
        attachment_rules: [],
        approval_rules: [],
      });
      setEditingItem(null);
      loadData();
    } catch (err) {
      setError("Failed to update leave entitlement");
    }
  };

  const addAttachmentRule = () => {
    setEntitlementForm((prev) => ({
      ...prev,
      attachment_rules: [
        ...prev.attachment_rules,
        {
          minDays: 0,
          maxDays: "",
          requirementType: "required",
          description: "",
        },
      ],
    }));
  };

  const removeAttachmentRule = (index: number) => {
    setEntitlementForm((prev) => ({
      ...prev,
      attachment_rules: prev.attachment_rules.filter((_, i) => i !== index),
    }));
  };

  const updateAttachmentRule = (index: number, field: string, value: any) => {
    setEntitlementForm((prev) => ({
      ...prev,
      attachment_rules: prev.attachment_rules.map((rule, i) =>
        i === index ? { ...rule, [field]: value } : rule,
      ),
    }));
  };

  const addApprovalRule = () => {
    setEntitlementForm((prev) => ({
      ...prev,
      approval_rules: [
        ...prev.approval_rules,
        {
          min_days: 0,
          max_days: null,
          approval_chain: [
            {
              level: 1,
              role: "hod",
              action_type: "recommend",
            },
          ],
        },
      ],
    }));
  };

  const removeApprovalRule = (ruleIndex: number) => {
    setEntitlementForm((prev) => ({
      ...prev,
      approval_rules: prev.approval_rules.filter((_, i) => i !== ruleIndex),
    }));
  };

  const updateApprovalRule = (ruleIndex: number, field: string, value: any) => {
    setEntitlementForm((prev) => ({
      ...prev,
      approval_rules: prev.approval_rules.map((rule, i) =>
        i === ruleIndex ? { ...rule, [field]: value } : rule,
      ),
    }));
  };

  const addApprovalStep = (ruleIndex: number) => {
    setEntitlementForm((prev) => ({
      ...prev,
      approval_rules: prev.approval_rules.map((rule, i) =>
        i === ruleIndex
          ? {
              ...rule,
              approval_chain: [
                ...rule.approval_chain,
                {
                  level: rule.approval_chain.length + 1,
                  role: "hod",
                  action_type: "recommend",
                },
              ],
            }
          : rule,
      ),
    }));
  };

  const removeApprovalStep = (ruleIndex: number, stepIndex: number) => {
    setEntitlementForm((prev) => ({
      ...prev,
      approval_rules: prev.approval_rules.map((rule, i) =>
        i === ruleIndex
          ? {
              ...rule,
              approval_chain: rule.approval_chain
                .filter((_, j) => j !== stepIndex)
                .map((step, j) => ({ ...step, level: j + 1 })),
            }
          : rule,
      ),
    }));
  };

  const updateApprovalStep = (
    ruleIndex: number,
    stepIndex: number,
    field: string,
    value: string,
  ) => {
    setEntitlementForm((prev) => ({
      ...prev,
      approval_rules: prev.approval_rules.map((rule, i) =>
        i === ruleIndex
          ? {
              ...rule,
              approval_chain: rule.approval_chain.map((step, j) =>
                j === stepIndex ? { ...step, [field]: value } : step,
              ),
            }
          : rule,
      ),
    }));
  };

  const handleDeleteEntitlement = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this entitlement?")) {
      try {
        await api.delete(`/admin/leave-entitlements/${id}`);
        setSuccess("Leave entitlement deleted successfully");
        loadData();
      } catch (err) {
        setError("Failed to delete leave entitlement");
      }
    }
  };

  // Vacation Period handlers
  const handleAddVacation = async () => {
    try {
      await api.post("/admin/vacation-periods", vacationForm);
      setSuccess("Vacation period added successfully");
      setOpenVacationDialog(false);
      setVacationForm({
        name: "",
        departmentId: "",
        categoryId: "",
        leaveTypeId: "",
        startDate: "",
        endDate: "",
        description: "",
      });
      loadData();
    } catch (err) {
      setError("Failed to add vacation period");
    }
  };

  const handleEditVacation = async (id: number) => {
    try {
      await api.put(`/admin/vacation-periods/${id}`, vacationForm);
      setSuccess("Vacation period updated successfully");
      setOpenVacationDialog(false);
      setVacationForm({
        name: "",
        departmentId: "",
        categoryId: "",
        leaveTypeId: "",
        startDate: "",
        endDate: "",
        description: "",
      });
      setEditingItem(null);
      loadData();
    } catch (err) {
      setError("Failed to update vacation period");
    }
  };

  const handleDeleteVacation = async (id: number) => {
    if (
      window.confirm("Are you sure you want to delete this vacation period?")
    ) {
      try {
        await api.delete(`/admin/vacation-periods/${id}`);
        setSuccess("Vacation period deleted successfully");
        loadData();
      } catch (err) {
        setError("Failed to delete vacation period");
      }
    }
  };

  // Attachment Requirement handlers
  const handleAddAttachment = async () => {
    try {
      await api.post("/admin/attachment-requirements", attachmentForm);
      setSuccess("Attachment requirement added successfully");
      setOpenAttachmentDialog(false);
      setAttachmentForm({
        leaveTypeId: "",
        minDays: 0,
        maxDays: "",
        requirementType: "not_required",
        description: "",
      });
      loadData();
    } catch (err) {
      setError("Failed to add attachment requirement");
    }
  };

  const handleEditAttachment = async (id: number) => {
    try {
      await api.put(`/admin/attachment-requirements/${id}`, attachmentForm);
      setSuccess("Attachment requirement updated successfully");
      setOpenAttachmentDialog(false);
      setAttachmentForm({
        leaveTypeId: "",
        minDays: 0,
        maxDays: "",
        requirementType: "not_required",
        description: "",
      });
      setEditingItem(null);
      loadData();
    } catch (err) {
      setError("Failed to update attachment requirement");
    }
  };

  const handleDeleteAttachment = async (id: number) => {
    if (
      window.confirm(
        "Are you sure you want to delete this attachment requirement?",
      )
    ) {
      try {
        await api.delete(`/admin/attachment-requirements/${id}`);
        setSuccess("Attachment requirement deleted successfully");
        loadData();
      } catch (err) {
        setError("Failed to delete attachment requirement");
      }
    }
  };

  // Leave Balance handlers
  const handleEditLeaveBalance = async (id: number) => {
    try {
      await api.put(`/admin/leave-balances/${id}`, {
        totalEntitlement: leaveBalanceForm.totalEntitlement,
        usedDays: leaveBalanceForm.usedDays,
      });
      setSuccess("Leave balance updated successfully");
      setOpenLeaveBalanceDialog(false);
      loadData();
    } catch (err) {
      setError("Failed to update leave balance");
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Paper sx={{ width: "100%", mb: 2 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Departments/Units" />
          <Tab label="Employee Categories" />
          <Tab label="Employees" />
          <Tab label="Leave Entitlements" />
          <Tab label="Leave Types" />
          <Tab label="Vacation Periods" />
          <Tab label="Holidays" />
          <Tab label="Grades" />
          <Tab label="Designations" />
          <Tab label="Employment History" />
          <Tab label="Leave Balances" />
          <Tab label="Notifications" />
          <Tab label="Audit Logs" />
          <Tab label="Database Backup" />
          <Tab label="Manual Leave Entry" />
        </Tabs>

        {/* Departments Tab */}
        <TabPanel value={tabValue} index={0}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenUnitDialog(true)}
            >
              Add Department
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {departments.map((dept) => (
                  <TableRow key={dept.id}>
                    <TableCell>{dept.name}</TableCell>
                    <TableCell>{dept.code}</TableCell>
                    <TableCell>{dept.description}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setEditingItem(dept);
                          setDepartmentForm({
                            name: dept.name,
                            code: dept.code || "",
                            description: dept.description,
                            headEmployeeId: dept.head_employee_id || "",
                            parentDepartmentId: dept.parent_department_id || "",
                          });
                          setOpenUnitDialog(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteDepartment(dept.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Categories Tab */}
        <TabPanel value={tabValue} index={1}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCategoryDialog(true)}
            >
              Add Category
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {categories.map((cat) => (
                  <TableRow key={cat.id}>
                    <TableCell>{cat.name}</TableCell>
                    <TableCell>{cat.description}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setEditingItem(cat);
                          setCategoryForm({
                            name: cat.name,
                            description: cat.description,
                          });
                          setOpenCategoryDialog(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteCategory(cat.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Employees Tab */}
        <TabPanel value={tabValue} index={2}>
          <Box sx={{ mb: 2, display: "flex", gap: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenEmployeeDialog(true)}
            >
              Add Employee
            </Button>
            <Button
              variant="outlined"
              startIcon={<UploadFileIcon />}
              onClick={() => setOpenBulkUploadDialog(true)}
            >
              Bulk Upload
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadAllEmployees}
              color="success"
            >
              Download All Employees
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Employee Code</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Designation</TableCell>
                  <TableCell>Grade</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>{employee.name}</TableCell>
                    <TableCell>{employee.email}</TableCell>
                    <TableCell>{employee.employee_code}</TableCell>
                    <TableCell>
                      {departments.find((d) => d.id === employee.department_id)
                        ?.name || ""}
                    </TableCell>
                    <TableCell>{employee.designation_name || ""}</TableCell>
                    <TableCell>{employee.grade_name || ""}</TableCell>
                    <TableCell>
                      {categories.find((c) => c.id === employee.category_id)
                        ?.name || ""}
                    </TableCell>
                    <TableCell>{employee.role}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setEditingItem(employee);
                          setEmployeeForm({
                            name: employee.name,
                            email: employee.email,
                            contact_number: employee.contact_number || "",
                            nic_number: employee.nic_number || "",
                            employee_code: employee.employee_code,
                            department_id: employee.department_id,
                            category_id: employee.category_id,
                            role: employee.role,
                            designation:
                              employee.designation_name ||
                              employee.designation ||
                              "",
                            designation_id: employee.designation_id || "",
                            grade_id: employee.grade_id || "",
                            join_date: employee.join_date
                              ? new Date(employee.join_date)
                                  .toISOString()
                                  .split("T")[0]
                              : "",
                          });
                          setOpenEmployeeDialog(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteEmployee(employee.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Leave Entitlements Tab */}
        <TabPanel value={tabValue} index={3}>
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Leave Entitlements Configuration
            </Typography>
            <Typography variant="body2">
              Configure specific leave rules for each employee category and
              leave type. This includes: maximum days per year, carry forward
              rules, approval requirements, attachment rules, and substitute
              requirements.
            </Typography>
          </Alert>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenEntitlementDialog(true)}
            >
              Add Leave Entitlement
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Max Days/Year</TableCell>
                  <TableCell>Carry Forward</TableCell>
                  <TableCell>Max Accumulation</TableCell>
                  <TableCell>Substitute Required</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entitlements.map((ent) => (
                  <TableRow key={ent.id}>
                    <TableCell>{ent.category_name}</TableCell>
                    <TableCell>{ent.leave_type_name}</TableCell>
                    <TableCell>{ent.max_days_per_year}</TableCell>
                    <TableCell>{ent.carry_forward_days}</TableCell>
                    <TableCell>{ent.max_accumulation}</TableCell>
                    <TableCell>
                      {ent.requires_substitute_acceptance === 1
                        ? "Yes - Must Accept"
                        : "No - Notify Only"}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setEditingItem(ent);
                          setEntitlementForm({
                            categoryId: ent.category_id,
                            leaveTypeId: ent.leave_type_id,
                            maxDaysPerYear: ent.max_days_per_year,
                            carryForwardDays: ent.carry_forward_days,
                            requires_substitute_acceptance: Number(
                              ent.requires_substitute_acceptance ?? 1,
                            ),
                            maxAccumulation: ent.max_accumulation,
                            attachment_rules: ent.attachment_rules
                              ? typeof ent.attachment_rules === "string"
                                ? JSON.parse(ent.attachment_rules)
                                : ent.attachment_rules
                              : [],
                            approval_rules: ent.approval_rules
                              ? typeof ent.approval_rules === "string"
                                ? JSON.parse(ent.approval_rules)
                                : ent.approval_rules
                              : [],
                          });
                          setOpenEntitlementDialog(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDeleteEntitlement(ent.id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Leave Types Tab */}
        <TabPanel value={tabValue} index={4}>
          <LeaveTypeManagement />
        </TabPanel>

        {/* Vacation Periods Tab - Index 5 */}
        <TabPanel value={tabValue} index={5}>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenVacationDialog(true)}
            >
              Add Vacation Period
            </Button>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Department</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {vacationPeriods.map((vp) => (
                  <TableRow key={vp.id}>
                    <TableCell>{vp.name}</TableCell>
                    <TableCell>{vp.department_id || "All"}</TableCell>
                    <TableCell>{vp.category_id || "All"}</TableCell>
                    <TableCell>{vp.leave_type_id}</TableCell>
                    <TableCell>
                      {new Date(vp.start_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {new Date(vp.end_date).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setEditingItem(vp);
                          setVacationForm({
                            name: vp.name,
                            departmentId: vp.department_id || "",
                            categoryId: vp.category_id || "",
                            leaveTypeId: vp.leave_type_id,
                            startDate: vp.start_date,
                            endDate: vp.end_date,
                            description: vp.description || "",
                          });
                          setOpenVacationDialog(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDeleteVacation(vp.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Holidays Tab */}
        <TabPanel value={tabValue} index={6}>
          <HolidayManagement
            onSuccess={(message) => setSuccess(message)}
            onError={(message) => setError(message)}
          />
        </TabPanel>

        {/* Grades Tab */}
        <TabPanel value={tabValue} index={7}>
          <GradeManagement />
        </TabPanel>

        {/* Designations Tab */}
        <TabPanel value={tabValue} index={8}>
          <DesignationManagement />
        </TabPanel>

        <TabPanel value={tabValue} index={9}>
          <EmploymentHistoryManagement />
        </TabPanel>

        {/* Leave Balances Tab */}
        <TabPanel value={tabValue} index={10}>
          <Box sx={{ mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              Leave Balances Management
            </Typography>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ display: "block" }}
            >
              Note: Leave balances are provisional and subject to change based
              on leaves taken this year.
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Leave Type</TableCell>
                  <TableCell>Year</TableCell>
                  <TableCell>Entitlement</TableCell>
                  <TableCell>Used</TableCell>
                  <TableCell>Available</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {leaveBalances.map((lb) => (
                  <TableRow key={lb.id}>
                    <TableCell>
                      {lb.employee_name} ({lb.employee_code})
                    </TableCell>
                    <TableCell>{lb.leave_type_name}</TableCell>
                    <TableCell>{lb.year}</TableCell>
                    <TableCell>{lb.total_entitlement}</TableCell>
                    <TableCell>{lb.used_days}</TableCell>
                    <TableCell>{lb.available_days}</TableCell>
                    <TableCell>
                      <IconButton
                        onClick={() => {
                          setEditingItem(lb);
                          setLeaveBalanceForm({
                            employeeId: lb.employee_id,
                            leaveTypeId: lb.leave_type_id,
                            totalEntitlement: lb.total_entitlement,
                            usedDays: lb.used_days,
                            year: lb.year,
                          });
                          setOpenLeaveBalanceDialog(true);
                        }}
                      >
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TabPanel>

        {/* Audit Logs Tab */}
        <TabPanel value={tabValue} index={11}>
          <RoleNotificationManagement />
        </TabPanel>

        {/* Audit Logs Tab */}
        <TabPanel value={tabValue} index={12}>
          <AuditLogManagement />
        </TabPanel>

        {/* Database Backup Tab */}
        <TabPanel value={tabValue} index={13}>
          <DatabaseBackupManagement />
        </TabPanel>

        {/* Manual Leave Entry Tab */}
        <TabPanel value={tabValue} index={14}>
          <ManualLeaveEntryManagement />
        </TabPanel>
      </Paper>

      {/* Department Dialog */}
      <Dialog open={openUnitDialog} onClose={handleCloseUnitDialog}>
        <DialogTitle>
          {editingItem ? "Edit Department" : "Add Department"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={departmentForm.name}
            onChange={(e) =>
              setDepartmentForm({ ...departmentForm, name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Code"
            fullWidth
            value={departmentForm.code}
            onChange={(e) =>
              setDepartmentForm({
                ...departmentForm,
                code: e.target.value.toUpperCase(),
              })
            }
            helperText="Unique code for this department (e.g., IT, HR, FIN)"
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={departmentForm.description}
            onChange={(e) =>
              setDepartmentForm({
                ...departmentForm,
                description: e.target.value,
              })
            }
          />
          <TextField
            select
            margin="dense"
            label="Department Head"
            fullWidth
            value={departmentForm.headEmployeeId}
            onChange={(e) =>
              setDepartmentForm({
                ...departmentForm,
                headEmployeeId: e.target.value,
              })
            }
          >
            <MenuItem value="">None</MenuItem>
            {employees.map((emp) => (
              <MenuItem key={emp.id} value={emp.id}>
                {emp.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="Parent Department"
            fullWidth
            value={departmentForm.parentDepartmentId}
            onChange={(e) =>
              setDepartmentForm({
                ...departmentForm,
                parentDepartmentId: e.target.value,
              })
            }
          >
            <MenuItem value="">None</MenuItem>
            {departments
              .filter((d) => d.id !== editingItem?.id)
              .map((dept) => (
                <MenuItem key={dept.id} value={dept.id}>
                  {dept.name}
                </MenuItem>
              ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnitDialog}>Cancel</Button>
          <Button
            onClick={() =>
              editingItem
                ? handleEditDepartment(editingItem.id)
                : handleAddDepartment()
            }
          >
            {editingItem ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Employee Dialog */}
      <Dialog
        open={openEmployeeDialog}
        onClose={() => setOpenEmployeeDialog(false)}
      >
        <DialogTitle>
          {editingItem ? "Edit Employee" : "Add Employee"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={employeeForm.name}
            onChange={(e) =>
              setEmployeeForm({ ...employeeForm, name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Email"
            type="email"
            fullWidth
            value={employeeForm.email}
            onChange={(e) =>
              setEmployeeForm({ ...employeeForm, email: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Contact Number"
            fullWidth
            value={employeeForm.contact_number}
            onChange={(e) =>
              setEmployeeForm({
                ...employeeForm,
                contact_number: e.target.value,
              })
            }
            helperText="Employee's phone number"
          />
          <TextField
            margin="dense"
            label="NIC Number"
            fullWidth
            value={employeeForm.nic_number}
            onChange={(e) =>
              setEmployeeForm({ ...employeeForm, nic_number: e.target.value })
            }
            helperText="National Identity Card number"
          />
          <TextField
            margin="dense"
            label="Employee Code"
            fullWidth
            value={employeeForm.employee_code}
            onChange={(e) =>
              setEmployeeForm({
                ...employeeForm,
                employee_code: e.target.value,
              })
            }
          />
          <TextField
            select
            margin="dense"
            label="Department"
            fullWidth
            value={employeeForm.department_id}
            onChange={(e) =>
              setEmployeeForm({
                ...employeeForm,
                department_id: e.target.value,
              })
            }
          >
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="Category"
            fullWidth
            value={employeeForm.category_id}
            onChange={(e) =>
              setEmployeeForm({ ...employeeForm, category_id: e.target.value })
            }
          >
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="Designation"
            fullWidth
            value={employeeForm.designation_id}
            onChange={(e) =>
              setEmployeeForm({
                ...employeeForm,
                designation_id: e.target.value,
                designation:
                  designations.find((d) => d.id === e.target.value)?.name || "",
              })
            }
          >
            {designations.map((des) => (
              <MenuItem key={des.id} value={des.id}>
                {des.name} ({des.code})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="Grade"
            fullWidth
            value={employeeForm.grade_id}
            onChange={(e) =>
              setEmployeeForm({ ...employeeForm, grade_id: e.target.value })
            }
          >
            {grades.map((g) => (
              <MenuItem key={g.id} value={g.id}>
                {g.name} ({g.code})
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Join Date"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={employeeForm.join_date}
            onChange={(e) =>
              setEmployeeForm({ ...employeeForm, join_date: e.target.value })
            }
          />
          <TextField
            select
            margin="dense"
            label="Role"
            fullWidth
            value={employeeForm.role}
            onChange={(e) =>
              setEmployeeForm({ ...employeeForm, role: e.target.value })
            }
          >
            <MenuItem value="staff">Staff</MenuItem>
            <MenuItem value="hod">HOD</MenuItem>
            <MenuItem value="dean">Dean</MenuItem>
            <MenuItem value="establishment">Establishment</MenuItem>
            <MenuItem value="vc">Vice Chancellor</MenuItem>
            <MenuItem value="registrar">Registrar</MenuItem>
            <MenuItem value="hr_manager">HR Manager</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEmployeeDialog(false)}>Cancel</Button>
          <Button
            onClick={() =>
              editingItem
                ? handleEditEmployee(editingItem.id)
                : handleAddEmployee()
            }
          >
            {editingItem ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Dialog */}
      <Dialog
        open={openCategoryDialog}
        onClose={() => setOpenCategoryDialog(false)}
      >
        <DialogTitle>
          {editingItem ? "Edit Category" : "Add Category"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={categoryForm.name}
            onChange={(e) =>
              setCategoryForm({ ...categoryForm, name: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={3}
            value={categoryForm.description}
            onChange={(e) =>
              setCategoryForm({ ...categoryForm, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCategoryDialog(false)}>Cancel</Button>
          <Button
            onClick={() =>
              editingItem
                ? handleEditCategory(editingItem.id)
                : handleAddCategory()
            }
          >
            {editingItem ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Entitlement Dialog */}
      <Dialog
        open={openEntitlementDialog}
        onClose={() => setOpenEntitlementDialog(false)}
      >
        <DialogTitle>
          {editingItem ? "Edit Leave Entitlement" : "Add Leave Entitlement"}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="Category"
            fullWidth
            value={entitlementForm.categoryId}
            onChange={(e) =>
              setEntitlementForm({
                ...entitlementForm,
                categoryId: e.target.value,
              })
            }
            disabled={!!editingItem}
          >
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="Leave Type"
            fullWidth
            value={entitlementForm.leaveTypeId}
            onChange={(e) =>
              setEntitlementForm({
                ...entitlementForm,
                leaveTypeId: e.target.value,
              })
            }
            disabled={!!editingItem}
          >
            {leaveTypes.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Max Days Per Year"
            type="number"
            fullWidth
            value={entitlementForm.maxDaysPerYear}
            onChange={(e) =>
              setEntitlementForm({
                ...entitlementForm,
                maxDaysPerYear: parseInt(e.target.value) || 0,
              })
            }
          />
          <TextField
            margin="dense"
            label="Carry Forward Days"
            type="number"
            fullWidth
            value={entitlementForm.carryForwardDays}
            onChange={(e) =>
              setEntitlementForm({
                ...entitlementForm,
                carryForwardDays: parseInt(e.target.value) || 0,
              })
            }
          />
          <TextField
            select
            margin="dense"
            label="Requires Substitute Acceptance"
            fullWidth
            value={entitlementForm.requires_substitute_acceptance}
            onChange={(e) =>
              setEntitlementForm({
                ...entitlementForm,
                requires_substitute_acceptance: parseInt(
                  e.target.value as string,
                ),
              })
            }
            helperText="Controls whether substitute must accept before leave goes to approval chain"
          >
            <MenuItem value={1}>
              YES - Substitute must accept before approval
            </MenuItem>
            <MenuItem value={0}>
              NO - Just notify substitute, go to manager
            </MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Max Accumulation"
            type="number"
            fullWidth
            value={entitlementForm.maxAccumulation}
            onChange={(e) =>
              setEntitlementForm({
                ...entitlementForm,
                maxAccumulation: parseInt(e.target.value) || 0,
              })
            }
          />

          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="h6">Attachment Rules</Typography>
            {entitlementForm.attachment_rules.map((rule, idx) => (
              <Box
                key={idx}
                sx={{
                  p: 2,
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  mb: 2,
                }}
              >
                <Box sx={{ display: "flex", gap: 1, mb: 1 }}>
                  <TextField
                    label="Min Days"
                    type="number"
                    size="small"
                    value={rule.minDays}
                    onChange={(e) =>
                      updateAttachmentRule(
                        idx,
                        "minDays",
                        parseFloat(e.target.value) || 0,
                      )
                    }
                  />
                  <TextField
                    label="Max Days"
                    type="number"
                    size="small"
                    value={rule.maxDays}
                    onChange={(e) =>
                      updateAttachmentRule(idx, "maxDays", e.target.value)
                    }
                    placeholder="Leave empty for no limit"
                  />
                  <TextField
                    select
                    label="Requirement"
                    size="small"
                    sx={{ minWidth: 120 }}
                    value={rule.requirementType}
                    onChange={(e) =>
                      updateAttachmentRule(
                        idx,
                        "requirementType",
                        e.target.value,
                      )
                    }
                  >
                    <MenuItem value="required">Required</MenuItem>
                    <MenuItem value="optional">Optional</MenuItem>
                    <MenuItem value="not_required">Not Required</MenuItem>
                  </TextField>
                  <IconButton
                    color="error"
                    onClick={() => removeAttachmentRule(idx)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
                <TextField
                  fullWidth
                  label="Description"
                  size="small"
                  value={rule.description}
                  onChange={(e) =>
                    updateAttachmentRule(idx, "description", e.target.value)
                  }
                />
              </Box>
            ))}
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddCircleIcon />}
              onClick={addAttachmentRule}
            >
              Add Attachment Rule
            </Button>
          </Box>

          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="h6">Approval Rules by Days</Typography>
            {entitlementForm.approval_rules.map((rule, ruleIdx) => (
              <Paper key={ruleIdx} sx={{ p: 2, mb: 2, bgcolor: "#f5f5f5" }}>
                <Box
                  sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}
                >
                  <TextField
                    label="Min Days"
                    type="number"
                    size="small"
                    value={rule.min_days}
                    onChange={(e) =>
                      updateApprovalRule(
                        ruleIdx,
                        "min_days",
                        parseInt(e.target.value) || 0,
                      )
                    }
                    sx={{ width: 100 }}
                  />
                  <TextField
                    label="Max Days"
                    type="number"
                    size="small"
                    value={rule.max_days || ""}
                    onChange={(e) =>
                      updateApprovalRule(
                        ruleIdx,
                        "max_days",
                        e.target.value ? parseInt(e.target.value) : null,
                      )
                    }
                    placeholder="Unlimited"
                    sx={{ width: 100 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Days: {rule.min_days} - {rule.max_days || "∞"}
                  </Typography>
                  <IconButton
                    color="error"
                    onClick={() => removeApprovalRule(ruleIdx)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>

                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Approval Chain:
                </Typography>
                {rule.approval_chain.map((step, stepIdx) => (
                  <Box
                    key={stepIdx}
                    sx={{
                      display: "flex",
                      gap: 1,
                      alignItems: "center",
                      mb: 1,
                      ml: 2,
                    }}
                  >
                    <Typography sx={{ minWidth: 60 }}>
                      Level {step.level}
                    </Typography>
                    <TextField
                      select
                      fullWidth
                      label="Role"
                      size="small"
                      value={step.role}
                      onChange={(e) =>
                        updateApprovalStep(
                          ruleIdx,
                          stepIdx,
                          "role",
                          e.target.value,
                        )
                      }
                    >
                      {[
                        "hod",
                        "dean",
                        "registrar",
                        "establishment",
                        "vc",
                        "hr_manager",
                        "admin",
                      ].map((role) => (
                        <MenuItem key={role} value={role}>
                          {role.toUpperCase()}
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      fullWidth
                      label="Action"
                      size="small"
                      value={step.action_type || "recommend"}
                      onChange={(e) =>
                        updateApprovalStep(
                          ruleIdx,
                          stepIdx,
                          "action_type",
                          e.target.value,
                        )
                      }
                    >
                      <MenuItem value="recommend">Recommend</MenuItem>
                      <MenuItem value="approve">Approve</MenuItem>
                    </TextField>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => removeApprovalStep(ruleIdx, stepIdx)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddCircleIcon />}
                  onClick={() => addApprovalStep(ruleIdx)}
                  sx={{ ml: 2 }}
                >
                  Add Step
                </Button>
              </Paper>
            ))}
            <Button
              variant="outlined"
              startIcon={<AddCircleIcon />}
              onClick={addApprovalRule}
            >
              Add Approval Rule
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEntitlementDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              editingItem
                ? handleEditEntitlement(editingItem.id)
                : handleAddEntitlement()
            }
          >
            {editingItem ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Vacation Period Dialog */}
      <Dialog
        open={openVacationDialog}
        onClose={() => setOpenVacationDialog(false)}
      >
        <DialogTitle>
          {editingItem ? "Edit Vacation Period" : "Add Vacation Period"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Name"
            fullWidth
            value={vacationForm.name}
            onChange={(e) =>
              setVacationForm({ ...vacationForm, name: e.target.value })
            }
          />
          <TextField
            select
            margin="dense"
            label="Department (Optional - leave empty for all)"
            fullWidth
            value={vacationForm.departmentId}
            onChange={(e) =>
              setVacationForm({ ...vacationForm, departmentId: e.target.value })
            }
          >
            <MenuItem value="">All Departments</MenuItem>
            {departments.map((dept) => (
              <MenuItem key={dept.id} value={dept.id}>
                {dept.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="Category (Optional - leave empty for all)"
            fullWidth
            value={vacationForm.categoryId}
            onChange={(e) =>
              setVacationForm({ ...vacationForm, categoryId: e.target.value })
            }
          >
            <MenuItem value="">All Categories</MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            margin="dense"
            label="Leave Type"
            fullWidth
            required
            value={vacationForm.leaveTypeId}
            onChange={(e) =>
              setVacationForm({ ...vacationForm, leaveTypeId: e.target.value })
            }
          >
            {leaveTypes.map((lt) => (
              <MenuItem key={lt.id} value={lt.id}>
                {lt.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Start Date"
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={vacationForm.startDate}
            onChange={(e) =>
              setVacationForm({ ...vacationForm, startDate: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="End Date"
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={vacationForm.endDate}
            onChange={(e) =>
              setVacationForm({ ...vacationForm, endDate: e.target.value })
            }
          />
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={vacationForm.description}
            onChange={(e) =>
              setVacationForm({ ...vacationForm, description: e.target.value })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenVacationDialog(false)}>Cancel</Button>
          <Button
            onClick={() =>
              editingItem
                ? handleEditVacation(editingItem.id)
                : handleAddVacation()
            }
          >
            {editingItem ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Attachment Requirement Dialog */}
      <Dialog
        open={openAttachmentDialog}
        onClose={() => setOpenAttachmentDialog(false)}
      >
        <DialogTitle>
          {editingItem ? "Edit Attachment Rule" : "Add Attachment Rule"}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            margin="dense"
            label="Leave Type"
            fullWidth
            required
            value={attachmentForm.leaveTypeId}
            onChange={(e) =>
              setAttachmentForm({
                ...attachmentForm,
                leaveTypeId: e.target.value,
              })
            }
          >
            {leaveTypes.map((lt) => (
              <MenuItem key={lt.id} value={lt.id}>
                {lt.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            margin="dense"
            label="Minimum Days"
            type="number"
            fullWidth
            value={attachmentForm.minDays}
            onChange={(e) =>
              setAttachmentForm({
                ...attachmentForm,
                minDays: parseFloat(e.target.value) || 0,
              })
            }
          />
          <TextField
            margin="dense"
            label="Maximum Days (leave empty for no limit)"
            type="number"
            fullWidth
            value={attachmentForm.maxDays}
            onChange={(e) =>
              setAttachmentForm({ ...attachmentForm, maxDays: e.target.value })
            }
          />
          <TextField
            select
            margin="dense"
            label="Requirement Type"
            fullWidth
            required
            value={attachmentForm.requirementType}
            onChange={(e) =>
              setAttachmentForm({
                ...attachmentForm,
                requirementType: e.target.value,
              })
            }
          >
            <MenuItem value="required">Required</MenuItem>
            <MenuItem value="optional">Optional</MenuItem>
            <MenuItem value="not_required">Not Required</MenuItem>
          </TextField>
          <TextField
            margin="dense"
            label="Description"
            fullWidth
            multiline
            rows={2}
            value={attachmentForm.description}
            onChange={(e) =>
              setAttachmentForm({
                ...attachmentForm,
                description: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAttachmentDialog(false)}>Cancel</Button>
          <Button
            onClick={() =>
              editingItem
                ? handleEditAttachment(editingItem.id)
                : handleAddAttachment()
            }
          >
            {editingItem ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Leave Balance Dialog */}
      <Dialog
        open={openLeaveBalanceDialog}
        onClose={() => setOpenLeaveBalanceDialog(false)}
      >
        <DialogTitle>Update Leave Balance</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Updating balance for <strong>{editingItem?.employee_name}</strong>{" "}
              - {editingItem?.leave_type_name} ({editingItem?.year})
            </Typography>
            <TextField
              margin="dense"
              label="Total Entitlement"
              type="number"
              fullWidth
              value={leaveBalanceForm.totalEntitlement}
              onChange={(e) =>
                setLeaveBalanceForm({
                  ...leaveBalanceForm,
                  totalEntitlement: parseFloat(e.target.value) || 0,
                })
              }
            />
            <TextField
              margin="dense"
              label="Used Days"
              type="number"
              fullWidth
              value={leaveBalanceForm.usedDays}
              onChange={(e) =>
                setLeaveBalanceForm({
                  ...leaveBalanceForm,
                  usedDays: parseFloat(e.target.value) || 0,
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLeaveBalanceDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => handleEditLeaveBalance(editingItem.id)}
            variant="contained"
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog
        open={openBulkUploadDialog}
        onClose={handleCloseBulkUpload}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Bulk Staff Upload</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a CSV file to add multiple employees at once. Download the
              template to see the required format.
            </Typography>

            <Box sx={{ mb: 3 }}>
              <input
                accept=".csv,.txt"
                style={{ display: "none" }}
                id="bulk-upload-file"
                type="file"
                onChange={handleFileChange}
              />
              <label htmlFor="bulk-upload-file">
                <Button
                  variant="outlined"
                  component="span"
                  startIcon={<UploadFileIcon />}
                  fullWidth
                >
                  {uploadFile ? uploadFile.name : "Select CSV File"}
                </Button>
              </label>
            </Box>

            {uploadResults && (
              <Box sx={{ mt: 2 }}>
                <Alert
                  severity={
                    uploadResults.summary.failed === 0 ? "success" : "warning"
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    <strong>Upload Summary:</strong>
                  </Typography>
                  <Typography variant="body2">
                    Total: {uploadResults.summary.total} | Success:{" "}
                    {uploadResults.summary.success} | Failed:{" "}
                    {uploadResults.summary.failed}
                  </Typography>
                </Alert>

                {uploadResults.created && uploadResults.created.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Successfully Created:
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                      {uploadResults.created.map((emp: any, idx: number) => (
                        <Typography
                          key={idx}
                          variant="body2"
                          color="success.main"
                        >
                          ✓ {emp.name} ({emp.employeeCode}) - {emp.email}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}

                {uploadResults.errors && uploadResults.errors.length > 0 && (
                  <Box>
                    <Typography
                      variant="subtitle2"
                      color="error"
                      sx={{ mb: 1 }}
                    >
                      Errors:
                    </Typography>
                    <Box sx={{ maxHeight: 200, overflowY: "auto" }}>
                      {uploadResults.errors.map((err: any, idx: number) => (
                        <Typography key={idx} variant="body2" color="error">
                          Line {err.line}: {err.employeeCode} - {err.error}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkUpload}>Close</Button>
          <Button
            onClick={handleBulkUpload}
            variant="contained"
            disabled={!uploadFile || uploading}
            startIcon={uploading ? null : <UploadFileIcon />}
          >
            {uploading ? "Uploading..." : "Upload"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbars for success/error messages */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AdminPanel;
